
import React, { useEffect, useState, useRef } from "react";
import { Typography, Select, MenuItem, InputLabel, Grid } from "@mui/material";
import GeneticFindingsReport from "./GeneticFindingsReport.js";
import { splitSNVandCNV } from "../../../create-report/sections/genetic_findings/utils.js";
import MappedText from "../../shared-components/mapText.js";
import { useSelector } from "react-redux";
import { getParticipant } from "../../../../../../services/api/get.js";
import { get_list_data } from "../../../../../../services/api/lists_api.js";
import { Trans } from 'react-i18next';

export default function GeneticFindingsSection({ report_id, data, handleSectionUpdate, analysisType }) {

    const token = useSelector(state => state.authorization.token);
    const { allDataApis } = useSelector((state) => state.generaltable);
    const urlBase = window.config.apis_configuration.api_phenostore;
    const selectedDir = allDataApis?.dirs;

    const [tableData, setTableData] = useState({ snv_data: [], cnv_data: [] });
    const [dataSNV, setGeneticFindingsDataSNV] = useState([]);
    const [dataCNV, setGeneticFindingsDataCNV] = useState([]);
    const [fusionDataAll, setFusionDataAll] = useState([]);
    const [fusionDataSelected, setFusionDataSelected] = useState([]);
    const [predefinedGeneList, setPredefinedGeneList] = useState([]);
    const [selectedGeneList, setSelectedGeneList] = useState("");
    const [selectedGeneListGenes, setSelectedGeneListGenes] = useState([]);
    const hasInitializedGeneList = useRef(false);






    useEffect(() => {
        const fetchData = async () => {
            if (!report_id) return;

            try {
                const response = await getParticipant(urlBase, token, report_id);
                const json = await response.json();

                if (analysisType === "somatic_fusion_analysis") {
                    if (Array.isArray(json.tagged_gene_fusions)) {
                        const fusionFiltered = json.tagged_gene_fusions.filter(f => f.origin === "somatic");
                        setFusionDataAll(fusionFiltered); 
                    }
                } else {

                    const allVariants = [];

                    const filterMatchOrigin = analysisType.toLowerCase().includes("somatic") ? "somatic" : "germline";
                    // SNV
                    // Filter variants by analysis type


                    if (json.genes && Array.isArray(json.genes)) {
                        json.genes.forEach(gene => {

                            if (gene.variants && Array.isArray(gene.variants)) {

                                // filtered variants by origin

                                let filtered = gene.variants.filter(variant => { return variant.origin?.toLowerCase() === filterMatchOrigin });
                                filtered.forEach(variant => {
                                    variant.geneName = gene.gene.name;
                                    allVariants.push(variant);
                                });
                            }
                        });
                    }

                    // CNV
                    if (json.regions && Array.isArray(json.regions)) {
                        json.regions.forEach(region => {
                            if (region.variants && Array.isArray(region.variants)) {

                                let filtered = region.variants.filter(variant => { return variant.origin?.toLowerCase() === filterMatchOrigin });
                                filtered.forEach(variant => {
                                    variant.region = region.region;
                                    allVariants.push(variant);
                                });
                            }
                        });
                    }

                    const initData = splitSNVandCNV(allVariants);
                    setTableData(initData);
                    setGeneticFindingsDataSNV(initData.snv_data);
                    setGeneticFindingsDataCNV(initData.cnv_data);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        fetchData();
    }, [urlBase, token, report_id,]);

    // Fetch predefined gene lists if werfen_endpoint exists
    useEffect(() => {
        if (!window.config.werfen_endpoint) return;

        const url = window.config.apis_configuration.api_nextgpap_server;
        fetch(url + '/get_all_lists', {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', "Authorization": token }
        })
        .then((response) => response.json())
        .then((data) => {
            setPredefinedGeneList(data.data.map(gl => ({name: gl.title, id: gl.id.toString()})));
        })
        .catch((error) => {
            console.error("Could not get predefined gene lists:", error);
        });
    }, []);

    // Set default selected gene list from DIR when both gene lists and DIR data are available
    // Only set once when DIR data and gene lists are first available, don't override user selections
    useEffect(() => {
        if (window.config.werfen_endpoint && selectedDir?.predefined_list && predefinedGeneList.length > 0 && !hasInitializedGeneList.current) {
            const dirPredefinedListId = selectedDir.predefined_list.toString();
            setSelectedGeneList(dirPredefinedListId);
            hasInitializedGeneList.current = true;
        }
    }, [selectedDir, predefinedGeneList]);

    // Fetch gene list data when selectedGeneList changes
    useEffect(() => {
        if (!window.config.werfen_endpoint || !selectedGeneList) {
            setSelectedGeneListGenes([]);
            return;
        }

        const url = window.config.apis_configuration.api_nextgpap_server;
        get_list_data(token, url, { "list_id": [selectedGeneList] })
            .then((response) => response.json())
            .then((json) => {
                if (json.data && json.data.length > 0 && json.data[0].data) {
                    const genes = json.data[0].data;
                    setSelectedGeneListGenes(genes);
                } else {
                    setSelectedGeneListGenes([]);
                }
            })
            .catch((error) => {
                console.error("Could not get gene list data:", error);
                setSelectedGeneListGenes([]);
            });
    }, [selectedGeneList]);


    const handleUpdate = (dataSelected, variantType) => {
        if (analysisType === "somatic_fusion_analysis") {
            if (variantType === "Gene Fusion") {
                setFusionDataSelected(dataSelected.rows);
                let sectionData = {
                    rows: dataSelected.rows,
                    conclusions: "",
                    recommendations: "",
                    geneListGenes: selectedGeneListGenes
                };
                handleSectionUpdate(sectionData, "Genetic_Findings");
            }
        } else {
            let updateData = [];
            if (variantType === "SNV") {
                setGeneticFindingsDataSNV(dataSelected.rows);
                updateData = [...dataSelected.rows, ...dataCNV];
            } else if (variantType === "CNV") {
                setGeneticFindingsDataCNV(dataSelected.rows);
                updateData = [...dataSNV, ...dataSelected.rows];
            }

            let sectionData = {
                rows: updateData,
                conclusions: "",
                recommendations: "",
                geneListGenes: selectedGeneListGenes
            };
            handleSectionUpdate(sectionData, "Genetic_Findings");
        }
    };

    // Update section data when gene list genes change
    useEffect(() => {
        if (!window.config.werfen_endpoint) return;

        if (analysisType === "somatic_fusion_analysis") {
            let sectionData = {
                rows: fusionDataSelected,
                conclusions: "",
                recommendations: "",
                geneListGenes: selectedGeneListGenes
            };
            handleSectionUpdate(sectionData, "Genetic_Findings");
        } else {
            let sectionData = {
                rows: [...dataSNV, ...dataCNV],
                conclusions: "",
                recommendations: "",
                geneListGenes: selectedGeneListGenes
            };
            handleSectionUpdate(sectionData, "Genetic_Findings");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedGeneListGenes]);

    // Reusable gene list selector component
    const geneListSelector = window.config.werfen_endpoint ? (
        <>
            <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
                <MappedText text="Gene List" />
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={4}>
                    <InputLabel>
                        <MappedText text={"Gene List"} />
                    </InputLabel>
                    <Select
                        fullWidth
                        value={selectedGeneList}
                        onChange={(e) => setSelectedGeneList(e.target.value)}
                    >
                        {predefinedGeneList.map((gl) => (
                            <MenuItem key={gl.id} value={gl.id}>
                                {gl.name}
                            </MenuItem>
                        ))}
                    </Select>
                </Grid>
            </Grid>
        </>
    ) : null;

    if (analysisType === "somatic_fusion_analysis") {
        return (
            <>
                <Typography variant="body1">
                    <Trans i18nKey="genetic_findings_tab_explanation">
                        These are the variants that were tagged in the <strong>Genetic Findings</strong> tab of this Interpretation Request. Check all the variants that you want to display in the final report.
                    </Trans>
                </Typography>

                <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
                    <MappedText text="Gene Fusions" />
                </Typography>
                <GeneticFindingsReport
                    handleUpdate={handleUpdate}
                    data={fusionDataAll}           
                    selectedData={fusionDataSelected}
                    variant_type="Gene Fusion"
                    analysisType={analysisType}
                />
                {geneListSelector}
            </>
        );
    } else {
        return <>
            <Typography variant="body1">
                <Trans i18nKey="genetic_findings_tab_explanation">
                    These are the variants that were tagged in the <strong>Genetic Findings</strong> tab of this Interpretation Request. Check all the variants that you want to display in the final report.
                </Trans>
            </Typography>

            <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
                <MappedText text="SNVs and InDels" />
            </Typography>
            <GeneticFindingsReport
                handleUpdate={handleUpdate}
                data={tableData.snv_data}
                variant_type="SNV"
                analysisType={analysisType}
            />
            <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
                <MappedText text="CNVs and SVs" />
            </Typography>
            <GeneticFindingsReport
                handleUpdate={handleUpdate}
                data={tableData.cnv_data}
                variant_type="CNV"
                analysisType={analysisType}
            />
            {geneListSelector}
        </>
    }

}